import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download, Copy, Check, Chrome, Puzzle, Pin, Shield, Sparkles,
  Monitor, ArrowRight, Play, Pause, RotateCcw, Clock, X
} from 'lucide-react';
import { toast } from 'sonner';

/* ------------------------------------------------------------------ */
/*  Bulletproof download trigger with VISIBLE feedback                  */
/*  - fetch + streaming reader -> live progress                         */
/*  - blob URL + anchor click -> browser's native download UI fires     */
/*  - fallback: hidden iframe + manual link                             */
/* ------------------------------------------------------------------ */
function resolveAbsoluteUrl(path) {
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  if (/^https?:\/\//i.test(path)) return path;
  if (backend) return backend.replace(/\/$/, '') + path;
  return path;
}

// Low-level: forces a blob to be saved to disk via anchor+download attribute.
function saveBlob(blob, filename) {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.rel = 'noopener';
  a.style.cssText = 'position:fixed;left:-9999px;top:-9999px;';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try { a.remove(); } catch (_) {}
    try { URL.revokeObjectURL(blobUrl); } catch (_) {}
  }, 4000);
}

// Simple fallback: anchor with direct URL (Content-Disposition: attachment).
function directLinkDownload(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.cssText = 'position:fixed;left:-9999px;top:-9999px;';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { try { a.remove(); } catch (_) {} }, 1500);
}

/**
 * Fetches the file with live progress and saves it to disk.
 * onProgress({ loaded, total, pct }) — callback with progress
 * Returns { ok, bytes, error }
 */
async function fetchAndSave(url, filename, onProgress) {
  try {
    const res = await fetch(url, { cache: 'no-store', credentials: 'omit' });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const total = parseInt(res.headers.get('content-length') || '0', 10) || 0;

    // Stream with progress if supported, else just get the blob
    if (res.body && typeof res.body.getReader === 'function') {
      const reader = res.body.getReader();
      const chunks = [];
      let loaded = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        loaded += value.length;
        onProgress?.({ loaded, total, pct: total ? (loaded / total) * 100 : 0 });
      }
      const blob = new Blob(chunks, { type: 'application/zip' });
      saveBlob(blob, filename);
      onProgress?.({ loaded, total: total || loaded, pct: 100 });
      return { ok: true, bytes: loaded };
    }

    const blob = await res.blob();
    saveBlob(blob, filename);
    onProgress?.({ loaded: blob.size, total: blob.size, pct: 100 });
    return { ok: true, bytes: blob.size };
  } catch (err) {
    return { ok: false, error: err?.message || String(err) };
  }
}

/* ------------------------------------------------------------------ */
/*  Browser detection (best-effort) — auto-select the right tab.       */
/* ------------------------------------------------------------------ */
function detectBrowser() {
  if (typeof navigator === 'undefined') return 'chrome';
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('edg/')) return 'edge';
  if (ua.includes('brave')) return 'brave';
  if (!ua.includes('chrome') && ua.includes('safari')) return 'safari';
  return 'chrome';
}

/* ------------------------------------------------------------------ */
/*  Browser config                                                      */
/* ------------------------------------------------------------------ */
const BROWSERS = {
  chrome:  { id: 'chrome',  label: 'Chrome',  extPage: 'chrome://extensions',  color: '#4285F4', shortLabel: 'Chrome' },
  edge:    { id: 'edge',    label: 'Edge',    extPage: 'edge://extensions',    color: '#0078D4', shortLabel: 'Edge' },
  brave:   { id: 'brave',   label: 'Brave',   extPage: 'brave://extensions',   color: '#FB542B', shortLabel: 'Brave' },
  safari:  { id: 'safari',  label: 'Safari',  extPage: 'Safari → Settings → Extensions', color: '#1B88CA', shortLabel: 'Safari' },
};

function CopyPill({ value, testId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // Clipboard permission denied — fallback: select via textarea
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success('Copied'); }
      catch (_) { toast.error('Copy failed — please copy manually'); }
      ta.remove();
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      className="group flex items-center gap-3 bg-gray-900 hover:bg-gray-800 text-white font-mono text-sm px-4 py-3 rounded-xl w-full transition-all border border-white/10 shadow-inner"
      data-testid={testId}
    >
      <span className="flex-1 text-left truncate select-all">{value}</span>
      <span className="shrink-0 flex items-center gap-1.5 text-xs font-sans font-semibold uppercase tracking-wider text-gray-400 group-hover:text-white transition-colors">
        {copied ? (
          <><Check className="w-4 h-4 text-green-400" /> Copied</>
        ) : (
          <><Copy className="w-4 h-4" /> Copy</>
        )}
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Animated walkthrough "video" — framer-motion driven                */
/* ------------------------------------------------------------------ */
const WALKTHROUGH_STEPS = [
  { key: 'download', label: 'Download .zip', duration: 3200 },
  { key: 'unzip',    label: 'Unzip on your computer', duration: 2800 },
  { key: 'openpage', label: 'Open extensions page', duration: 3200 },
  { key: 'devmode',  label: 'Enable Developer Mode', duration: 2800 },
  { key: 'load',     label: 'Click "Load Unpacked" → select folder', duration: 3500 },
  { key: 'pin',      label: 'Pin extension to toolbar', duration: 3000 },
  { key: 'done',     label: 'Done!', duration: 2500 },
];

function WalkthroughVideo({ browser }) {
  const [playing, setPlaying] = useState(true);
  const [stepIdx, setStepIdx] = useState(0);
  const [tick, setTick] = useState(0);
  const startedAt = useRef(Date.now());

  const step = WALKTHROUGH_STEPS[stepIdx];
  const stepDuration = step.duration;

  useEffect(() => {
    if (!playing) return;
    startedAt.current = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startedAt.current;
      setTick(elapsed);
      if (elapsed >= stepDuration) {
        setStepIdx(i => (i + 1) % WALKTHROUGH_STEPS.length);
      }
    }, 80);
    return () => clearInterval(interval);
  }, [playing, stepIdx, stepDuration]);

  const restart = () => { setStepIdx(0); setTick(0); setPlaying(true); startedAt.current = Date.now(); };
  const totalDuration = WALKTHROUGH_STEPS.reduce((s, x) => s + x.duration, 0);
  const overallProgress = WALKTHROUGH_STEPS.slice(0, stepIdx).reduce((s, x) => s + x.duration, 0) + tick;

  return (
    <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#1C1917] via-[#141211] to-black border border-white/10 shadow-2xl" data-testid="install-walkthrough-video">
      {/* Browser chrome mockup */}
      <div className="flex items-center gap-2 px-4 py-3 bg-[#1C1917]/80 border-b border-white/5">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500/70" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
          <div className="w-3 h-3 rounded-full bg-green-500/70" />
        </div>
        <div className="flex-1 mx-4 px-3 py-1 rounded-md bg-slate-800/70 text-xs text-slate-400 font-mono truncate">
          {browser.extPage}
        </div>
        <div className="text-xs text-slate-500 font-sans">{browser.label}</div>
      </div>

      {/* Stage */}
      <div className="relative h-[340px] sm:h-[400px] overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={step.key}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.35 }}
            className="absolute inset-0 p-6 sm:p-10 flex items-center justify-center"
          >
            {step.key === 'download' && (
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: [0.9, 1.05, 1] }}
                  transition={{ duration: 0.8, repeat: Infinity, repeatType: 'reverse' }}
                  className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2C4A3B] to-[#1E3329] shadow-xl mb-4"
                >
                  <Download className="w-10 h-10 text-white" />
                </motion.div>
                <p className="text-white/90 font-semibold text-lg">plexura-ai-scribe-extension.zip</p>
                <div className="mt-4 w-56 h-2 rounded-full bg-white/10 overflow-hidden mx-auto">
                  <motion.div
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: stepDuration / 1000, ease: 'linear' }}
                    className="h-full bg-gradient-to-r from-green-400 to-emerald-500"
                  />
                </div>
                <p className="text-xs text-white/50 mt-3">Downloading 21 KB</p>
              </div>
            )}

            {step.key === 'unzip' && (
              <div className="text-center">
                <motion.div className="relative mx-auto w-56 h-40">
                  <motion.div
                    initial={{ y: 0, opacity: 1 }}
                    animate={{ y: -20, opacity: 0.3 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    className="absolute left-1/2 -translate-x-1/2 w-36 h-28 rounded-xl bg-gradient-to-br from-[#2C4A3B]/70 to-[#1E3329]/70 flex items-center justify-center border border-white/20 shadow-xl"
                  >
                    <div className="text-white text-xs font-mono">.zip</div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1, duration: 0.5 }}
                    className="absolute left-1/2 -translate-x-1/2 top-8 w-40 h-24 rounded-lg bg-white/5 border border-white/10 p-3 text-[10px] text-white/70 font-mono space-y-1 shadow-xl"
                  >
                    <div>📁 manifest.json</div>
                    <div>📁 background.js</div>
                    <div>📁 content.js</div>
                    <div>📁 popup.html</div>
                    <div>📁 icons/</div>
                  </motion.div>
                </motion.div>
                <p className="text-white/80 text-sm mt-2">Unzipped to your Downloads folder</p>
              </div>
            )}

            {step.key === 'openpage' && (
              <div className="w-full max-w-md">
                <div className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm">
                  <div className="text-[11px] text-white/50 uppercase font-semibold mb-2 tracking-wider">Address bar</div>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 1.2, ease: 'easeOut' }}
                    className="overflow-hidden"
                  >
                    <div className="bg-slate-800 font-mono text-sm text-cyan-300 rounded-md px-3 py-2 border border-white/10 whitespace-nowrap">
                      {browser.extPage}
                    </div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.4 }}
                    className="mt-3 flex items-center gap-2 text-[11px] text-white/60"
                  >
                    <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
                    Press Enter
                  </motion.div>
                </div>
              </div>
            )}

            {step.key === 'devmode' && (
              <div className="w-full max-w-md">
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-white font-semibold">Extensions</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-white/70">Developer mode</span>
                      <motion.div
                        initial={{ backgroundColor: 'rgba(255,255,255,0.12)' }}
                        animate={{ backgroundColor: '#8B5CF6' }}
                        transition={{ delay: 0.8, duration: 0.35 }}
                        className="w-11 h-6 rounded-full relative"
                      >
                        <motion.div
                          initial={{ x: 2 }}
                          animate={{ x: 22 }}
                          transition={{ delay: 0.8, duration: 0.35, ease: 'easeOut' }}
                          className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-lg"
                        />
                      </motion.div>
                    </div>
                  </div>
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.3 }}
                    className="flex gap-2"
                  >
                    <div className="px-3 py-1.5 bg-white/10 text-white text-xs rounded-md border border-white/10">Load unpacked</div>
                    <div className="px-3 py-1.5 bg-white/5 text-white/60 text-xs rounded-md border border-white/10">Pack extension</div>
                    <div className="px-3 py-1.5 bg-white/5 text-white/60 text-xs rounded-md border border-white/10">Update</div>
                  </motion.div>
                </div>
              </div>
            )}

            {step.key === 'load' && (
              <div className="w-full max-w-md">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white rounded-xl p-5 shadow-2xl"
                >
                  <div className="text-slate-800 font-semibold text-sm mb-3">Select the unzipped folder</div>
                  <div className="space-y-1.5 text-xs font-mono text-slate-600">
                    <div>📁 Downloads</div>
                    <div className="pl-4 flex items-center gap-2">
                      <motion.div
                        initial={{ backgroundColor: '#fff' }}
                        animate={{ backgroundColor: '#EDE9FE' }}
                        transition={{ delay: 0.6, duration: 0.4 }}
                        className="flex-1 px-2 py-1 rounded"
                      >
                        <span className="font-bold text-[#C47A5E]">📁 plexura-ai-scribe-extension</span>
                      </motion.div>
                    </div>
                    <div className="pl-4">📁 other-files</div>
                  </div>
                  <motion.button
                    initial={{ opacity: 0.5 }}
                    animate={{ opacity: 1, scale: [1, 1.04, 1] }}
                    transition={{ delay: 1.4, duration: 0.4 }}
                    className="mt-4 w-full bg-[#2C4A3B] text-white font-semibold text-sm py-2 rounded-lg"
                  >
                    Select Folder
                  </motion.button>
                </motion.div>
              </div>
            )}

            {step.key === 'pin' && (
              <div className="w-full max-w-md">
                <div className="bg-slate-800/60 border border-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <div className="flex items-center justify-end gap-2 mb-4">
                    <Puzzle className="w-5 h-5 text-white/70" />
                    <span className="text-xs text-white/60">Extensions menu</span>
                  </div>
                  <div className="bg-[#1C1917] rounded-lg p-3 space-y-2">
                    <motion.div
                      initial={{ backgroundColor: 'transparent' }}
                      animate={{ backgroundColor: 'rgba(139, 92, 246, 0.2)' }}
                      transition={{ delay: 0.5, duration: 0.4 }}
                      className="flex items-center justify-between px-2 py-1.5 rounded"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-gradient-to-br from-[#2C4A3B] to-[#1E3329] flex items-center justify-center text-white font-black text-[10px]">P</div>
                        <span className="text-xs text-white/90">Plexura AI Scribe</span>
                      </div>
                      <motion.div
                        initial={{ scale: 1, color: '#94A3B8' }}
                        animate={{ scale: [1, 1.3, 1], color: '#8B5CF6' }}
                        transition={{ delay: 1.1, duration: 0.4 }}
                      >
                        <Pin className="w-4 h-4" style={{ transform: 'rotate(-45deg)' }} />
                      </motion.div>
                    </motion.div>
                  </div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.6 }}
                    className="mt-3 text-[11px] text-white/60 text-center"
                  >
                    Click the pin icon to keep it visible in your toolbar
                  </motion.div>
                </div>
              </div>
            )}

            {step.key === 'done' && (
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', damping: 12 }}
                  className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 shadow-2xl mb-4"
                >
                  <Check className="w-12 h-12 text-white" strokeWidth={3} />
                </motion.div>
                <h3 className="text-white text-2xl font-bold">You're ready!</h3>
                <p className="text-white/60 text-sm mt-2">Open your EHR, record a session, push notes with one click.</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Step pill */}
        <div className="absolute top-4 left-4 right-4 flex items-center gap-2 pointer-events-none">
          <div className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-[11px] text-white/80 font-medium">
            Step {stepIdx + 1} of {WALKTHROUGH_STEPS.length} · {step.label}
          </div>
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex items-center gap-3 px-4 py-3 bg-black/40 border-t border-white/5">
        <button
          type="button"
          onClick={() => setPlaying(p => !p)}
          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          data-testid="walkthrough-playpause"
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        <button
          type="button"
          onClick={restart}
          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          data-testid="walkthrough-restart"
          aria-label="Restart"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
        <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-[#2C4A3B] to-[#6B8E7B]"
            style={{ width: `${(overallProgress / totalDuration) * 100}%` }}
            transition={{ duration: 0.1, ease: 'linear' }}
          />
        </div>
        <div className="text-[10px] text-white/50 font-mono tabular-nums">
          {Math.floor(overallProgress / 1000).toString().padStart(2, '0')}s / {Math.floor(totalDuration / 1000)}s
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Download progress modal — makes the download visible                */
/* ------------------------------------------------------------------ */
function formatBytes(n) {
  if (!n && n !== 0) return '0 B';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  return (n / 1024 / 1024).toFixed(2) + ' MB';
}

function DownloadModal({ open, state, progress, filename, directUrl, error, onClose, onRetry }) {
  if (!open) return null;
  const isDone = state === 'saved';
  const isError = state === 'error';
  const isDownloading = state === 'downloading' || state === 'starting';

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="download-modal"
      role="dialog"
      aria-modal="true"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
      >
        {/* Header icon */}
        <div className="relative h-28 bg-gradient-to-br from-[#2C4A3B] via-[#1E3329] to-[#1C1917] flex items-center justify-center overflow-hidden">
          <motion.div
            key={state}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', damping: 14 }}
            className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-xl ${
              isDone ? 'bg-green-500' : isError ? 'bg-red-500' : 'bg-white/20 backdrop-blur'
            }`}
          >
            {isDone ? (
              <Check className="w-9 h-9 text-white" strokeWidth={3} />
            ) : isError ? (
              <X className="w-9 h-9 text-white" strokeWidth={3} />
            ) : (
              <motion.div animate={{ y: [-2, 2, -2] }} transition={{ duration: 1.2, repeat: Infinity }}>
                <Download className="w-9 h-9 text-white" />
              </motion.div>
            )}
          </motion.div>
          {/* Decorative dots */}
          {isDownloading && (
            <div className="absolute inset-0 pointer-events-none">
              {[0, 1, 2, 3, 4].map(i => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40, x: (i - 2) * 30 }}
                  animate={{ opacity: [0, 1, 0], y: -30 }}
                  transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.25 }}
                  className="absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full bg-white/60"
                />
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            aria-label="Close"
            data-testid="download-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          <h3 className="text-xl font-bold text-slate-900 mb-1">
            {isDone ? 'Download saved!' : isError ? 'Download blocked' : 'Downloading extension…'}
          </h3>
          <p className="text-sm text-slate-500 font-mono truncate mb-4">{filename}</p>

          {/* Progress bar */}
          {(isDownloading || isDone) && (
            <div className="mb-4">
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <motion.div
                  className={`h-full rounded-full ${isDone ? 'bg-green-500' : 'bg-gradient-to-r from-[#2C4A3B] to-[#1E3329]'}`}
                  animate={{ width: `${Math.min(100, progress?.pct || 0)}%` }}
                  transition={{ duration: 0.3, ease: 'linear' }}
                />
              </div>
              <div className="flex items-center justify-between mt-2 text-xs text-slate-500 tabular-nums">
                <span>{formatBytes(progress?.loaded || 0)}{progress?.total ? ` / ${formatBytes(progress.total)}` : ''}</span>
                <span>{Math.round(progress?.pct || 0)}%</span>
              </div>
            </div>
          )}

          {/* Done message */}
          {isDone && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-green-800 font-semibold flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Saved to your <strong>Downloads</strong> folder. Unzip it, then follow Step 2 below.</span>
              </p>
            </div>
          )}

          {/* Error message + retry */}
          {isError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-800 font-semibold mb-1">We couldn't start the download automatically.</p>
              <p className="text-xs text-red-600 break-all">{error}</p>
            </div>
          )}

          {/* Always show a manual fallback link */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2">
              Not seeing the download?
            </p>
            <a
              href={directUrl}
              download={filename}
              rel="noopener"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#C47A5E] hover:text-[#A56243] transition-colors"
              data-testid="download-modal-manual-link"
            >
              <Download className="w-4 h-4" />
              Right-click here &amp; "Save Link As…"
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Actions */}
          <div className="flex gap-2 mt-5">
            {isError && (
              <button
                type="button"
                onClick={onRetry}
                className="flex-1 bg-[#2C4A3B] hover:bg-[#1E3329] text-white font-semibold py-2.5 rounded-xl transition-colors"
                data-testid="download-modal-retry"
              >
                Try again
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className={`${isError ? 'flex-1' : 'w-full'} bg-[#F9F6F0] hover:bg-[#E3E8E4] text-slate-700 font-semibold py-2.5 rounded-xl transition-colors`}
              data-testid="download-modal-ok"
            >
              {isDone ? 'Continue to setup →' : 'Close'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
function CountdownStrip({ running }) {
  const [seconds, setSeconds] = useState(60);
  useEffect(() => {
    if (!running) return;
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [running, seconds]);
  const pct = Math.max(0, (seconds / 60) * 100);
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 text-white/90">
        <Clock className="w-4 h-4" />
        <span className="text-sm font-semibold tabular-nums">{seconds}s</span>
      </div>
      <div className="flex-1 max-w-xs h-1.5 rounded-full bg-white/15 overflow-hidden">
        <motion.div
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: 'linear' }}
          className="h-full bg-gradient-to-r from-green-400 to-emerald-500"
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */
export default function InstallPage() {
  const navigate = useNavigate();
  const [browserKey, setBrowserKey] = useState('chrome');
  const [downloaded, setDownloaded] = useState(false);
  const [countdownRunning, setCountdownRunning] = useState(false);

  // Download modal state
  const [dlOpen, setDlOpen] = useState(false);
  const [dlState, setDlState] = useState('idle'); // idle | starting | downloading | saved | error
  const [dlProgress, setDlProgress] = useState({ loaded: 0, total: 0, pct: 0 });
  const [dlError, setDlError] = useState('');

  useEffect(() => {
    setBrowserKey(detectBrowser());
  }, []);

  const browser = BROWSERS[browserKey];
  const isSafari = browserKey === 'safari';

  const currentPath = isSafari ? '/api/extension/download/safari' : '/api/extension/download/chrome';
  const currentFilename = isSafari ? 'plexura-safari-extension.zip' : 'plexura-ai-scribe-extension.zip';
  const currentAbsoluteUrl = resolveAbsoluteUrl(currentPath);

  const startDownload = async () => {
    const url = currentAbsoluteUrl + (currentAbsoluteUrl.includes('?') ? '&' : '?') + 't=' + Date.now();
    setDlOpen(true);
    setDlState('starting');
    setDlError('');
    setDlProgress({ loaded: 0, total: 0, pct: 0 });
    setCountdownRunning(true);

    // Immediately try the directLink approach too (covers browsers where
    // fetch blob is blocked but same-site downloads work). Silent no-op
    // if blocked; serves as a warm-up.
    try { directLinkDownload(url, currentFilename); } catch (_) {}

    setDlState('downloading');
    const result = await fetchAndSave(url, currentFilename, (p) => setDlProgress(p));

    if (result.ok) {
      setDlState('saved');
      setDownloaded(true);
      toast.success(`${currentFilename} saved to your Downloads folder`);
    } else {
      setDlState('error');
      setDlError(result.error || 'Unknown error');
      toast.error('Automatic download blocked — use the manual link in the dialog');
    }
  };

  const closeDlModal = () => {
    setDlOpen(false);
    // Keep countdown running if we successfully downloaded
    if (dlState !== 'saved') setCountdownRunning(false);
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Manrope', sans-serif", background: '#F9F6F0' }} data-testid="install-page">
      {/* Download progress modal (visible feedback) */}
      <DownloadModal
        open={dlOpen}
        state={dlState}
        progress={dlProgress}
        filename={currentFilename}
        directUrl={currentAbsoluteUrl}
        error={dlError}
        onClose={closeDlModal}
        onRetry={startDownload}
      />

      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-gradient-to-r from-[#1E3329] via-[#2C4A3B] to-[#1C1917] text-white border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-6 flex-wrap">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 font-bold text-white/95 hover:text-white transition-colors"
            data-testid="install-home-link"
          >
            <Shield className="w-5 h-5" />
            <span>Plexura AI Scribe</span>
          </button>
          {countdownRunning ? (
            <CountdownStrip running={countdownRunning} />
          ) : (
            <div className="flex items-center gap-2 text-sm text-white/85">
              <Sparkles className="w-4 h-4" />
              Install in under 60 seconds
            </div>
          )}
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-6 sm:pt-16">
        {/* Upgrade notice — shown to everyone since we can't detect installed version from web page */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-8 flex items-start gap-3 rounded-2xl p-4"
          style={{ background: '#E3E8E4', border: '1px solid #C5D2CA' }}
          data-testid="install-upgrade-notice"
        >
          <Sparkles className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#2C4A3B' }} />
          <div className="flex-1">
            <p className="text-sm font-bold" style={{ color: '#1E3329' }}>
              Version 2.16.0 — Smart fill: REPLACE stale duplicates instead of pushing twice
            </p>
            <p className="text-xs mt-1" style={{ color: '#3F5E4F' }}>
              If you installed an earlier version, please <strong>re-download and reload</strong> the extension: go to <code className="bg-[#E3E8E4] px-1.5 py-0.5 rounded font-mono text-[11px]">{browser.extPage}</code>, remove the old version, then load the new unpacked folder. v2.16.0 fixes a SimplePractice / EHR duplication bug — when you push a note and the EHR's rich-text editor still holds the previous push (because the field wasn't fully cleared), the extension now <strong>replaces</strong> the stale content instead of pushing a second copy alongside it. Manual content typed by the provider is still preserved (new note prepends above with a blank-line separator). Also keeps the v2.15.x host-priority credential fetching, the 6 Tebra note templates with per-template "Teach this layout" caching, plus the new BIRP / DAP / GIRP progress-note formats and the searchable + grouped template picker.
            </p>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-5 gap-8 items-center">
          <div className="md:col-span-3 space-y-5">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E3E8E4] border border-[#E3E8E4] text-xs font-semibold text-[#C47A5E]"
            >
              <Puzzle className="w-3.5 h-3.5" />
              One-click install · Auto-pin · Auto-auth
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] text-slate-900 tracking-tight"
            >
              Your EHR connection,
              <span className="block bg-gradient-to-r from-[#2C4A3B] via-[#1E3329] to-[#1C1917] bg-clip-text text-transparent">
                installed in 60 seconds.
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-slate-600 max-w-xl"
            >
              Download the Universal EHR Extension, pin it, and push your AI-generated notes directly into SimplePractice, Epic, Tebra, and 15+ EHRs — no copy-paste.
            </motion.p>

            {/* Primary CTA */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="flex flex-wrap items-center gap-4"
            >
              <button
                type="button"
                onClick={startDownload}
                className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-[#2C4A3B] to-[#1E3329] hover:from-[#1E3329] hover:to-[#141211] text-white font-bold px-7 py-4 rounded-2xl shadow-xl shadow-[#2C4A3B]/25 transition-all hover:scale-[1.02] active:scale-100"
                data-testid="install-primary-download"
              >
                <Download className="w-5 h-5" />
                {downloaded ? 'Download again' : `Download for ${browser.shortLabel}`}
                <ArrowRight className="w-5 h-5 opacity-0 -ml-3 group-hover:opacity-100 group-hover:ml-0 transition-all" />
              </button>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Shield className="w-4 h-4" />
                21 KB · HIPAA-compliant · Manifest V3
              </div>
            </motion.div>

            {/* Browser selector */}
            <div className="flex flex-wrap gap-2 pt-2" data-testid="install-browser-selector">
              {Object.values(BROWSERS).map(b => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setBrowserKey(b.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
                    browserKey === b.id
                      ? 'bg-[#1C1917] text-white border-slate-900 shadow-md'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`install-browser-${b.id}`}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: b.color }} />
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <WalkthroughVideo browser={browser} />
          </div>
        </div>
      </section>

      {/* Steps grid */}
      <section className="max-w-6xl mx-auto px-6 py-12" data-testid="install-steps">
        <div className="mb-8">
          <h2 className="text-sm uppercase tracking-wider text-[#2C4A3B] font-bold mb-1">The 60-second setup</h2>
          <p className="text-3xl font-black text-slate-900">Four quick steps</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* Step 1 */}
          <StepCard num={1} title="Download the extension" time="5s">
            <p className="text-sm text-slate-600 mb-4">
              Already downloaded? Jump to step 2. Otherwise click the big button above or here:
            </p>
            <button
              type="button"
              onClick={startDownload}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1C1917] hover:bg-[#141211] text-white font-semibold px-4 py-3 rounded-xl transition-colors"
              data-testid="install-step1-download"
            >
              <Download className="w-4 h-4" />
              Download {browser.shortLabel} extension (.zip)
            </button>
            <p className="text-xs text-slate-500 mt-3">
              {isSafari
                ? 'Safari requires Xcode conversion — see detailed steps below.'
                : 'Then unzip the file into any folder on your computer.'}
            </p>
          </StepCard>

          {/* Step 2 */}
          <StepCard num={2} title="Open the extensions page" time="5s">
            <p className="text-sm text-slate-600 mb-4">
              Copy this address and paste it into a new browser tab, then press Enter:
            </p>
            {isSafari ? (
              <div className="bg-[#1C1917] text-white/90 text-sm p-4 rounded-xl font-mono leading-relaxed">
                Safari → <span className="text-cyan-300">Settings</span> → <span className="text-cyan-300">Extensions</span>
                <p className="text-xs text-white/50 font-sans mt-2">
                  Safari doesn't have a direct <code>safari://extensions</code> URL — use the menu path instead.
                </p>
              </div>
            ) : (
              <CopyPill value={browser.extPage} testId="install-copy-extpage" />
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.values(BROWSERS).filter(b => b.id !== browserKey && b.id !== 'safari').map(b => (
                <CopyMiniPill key={b.id} browser={b} />
              ))}
            </div>
          </StepCard>

          {/* Step 3 */}
          <StepCard num={3} title="Enable Developer Mode" time="3s">
            <p className="text-sm text-slate-600 mb-3">
              Toggle <strong>Developer mode</strong> in the top-right corner.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Developer mode</span>
              <div className="w-11 h-6 bg-[#2C4A3B] rounded-full relative">
                <div className="absolute right-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow" />
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              This allows loading extensions from a local folder — required for our unpacked distribution.
            </p>
          </StepCard>

          {/* Step 4 */}
          <StepCard num={4} title='Click "Load unpacked" → Pin it' time="15s">
            <p className="text-sm text-slate-600 mb-3">
              Click <strong>"Load unpacked"</strong> and select the unzipped folder. Then click the <Puzzle className="w-3.5 h-3.5 inline text-slate-600" /> puzzle icon in your toolbar and pin Plexura AI Scribe.
            </p>
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
              <div className="shrink-0 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                <Check className="w-4 h-4 text-white" strokeWidth={3} />
              </div>
              <div>
                <p className="text-sm font-bold text-green-900">You're done!</p>
                <p className="text-xs text-green-700 mt-0.5">
                  Return to Plexura and open your EHR in another tab — credentials auto-populate, notes push with one click.
                </p>
              </div>
            </div>
          </StepCard>
        </div>

        {/* Back to app */}
        <div className="mt-10 text-center">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#C47A5E] hover:text-[#A56243] transition-colors"
            data-testid="install-back-to-app"
          >
            <ArrowRight className="w-4 h-4" />
            Back to Dashboard
          </button>
        </div>
      </section>
    </div>
  );
}

function StepCard({ num, title, time, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5 }}
      className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
      data-testid={`install-step-card-${num}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#2C4A3B] to-[#1E3329] text-white font-bold flex items-center justify-center shadow-md">
            {num}
          </div>
          <h3 className="font-bold text-slate-900 text-lg">{title}</h3>
        </div>
        <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{time}</span>
      </div>
      {children}
    </motion.div>
  );
}

function CopyMiniPill({ browser }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(browser.extPage);
      setCopied(true);
      toast.success(`Copied: ${browser.extPage}`);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error('Copy failed');
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      className="inline-flex items-center gap-1.5 text-xs font-mono px-2.5 py-1.5 rounded-lg bg-[#F9F6F0] hover:bg-[#E3E8E4] text-slate-700 transition-colors border border-slate-200"
      data-testid={`install-copy-mini-${browser.id}`}
    >
      {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
      {browser.extPage}
    </button>
  );
}
